import { matchesPath } from "./matchesPath";

// Minimal structural shape shared by PageNavMenu's PageNavLink and the drawer's
// PageNavDrawerLink. Both menus resolve the active entry the same way, so the
// traversal lives here once - a divergence between desktop and drawer
// highlighting would be a hard-to-spot bug.
export interface ActiveNavLink {
  href: string;
  // Stable key used when href isn't usable (e.g. a group whose href is empty).
  id?: string;
  children?: ActiveNavLink[];
}

export interface ActiveNavMatch {
  // Key (id ?? href) of the top-level entry that owns the active state. For a
  // group this is the group's key; the child that matched lives in `href`.
  key: string;
  // The href that actually matched the pathname - a group child's href, or the
  // entry's own href for a flat link.
  href: string;
}

const linkKey = (link: ActiveNavLink): string => link.id ?? link.href;

/**
 * Resolves which nav entry is active for the current pathname, or for an
 * explicit `activeHrefOverride` when navigation is driven by state rather than
 * the URL.
 *
 * Longest matching href wins, so a child nested under `matchSubPaths` beats a
 * shorter ancestor. Groups have no href of their own; they inherit the match
 * from their children. Returns `null` when nothing is active.
 */
export function findActiveNav(
  links: ActiveNavLink[],
  pathname: string,
  {
    matchSubPaths,
    activeHrefOverride,
  }: { matchSubPaths: boolean; activeHrefOverride?: string | null }
): ActiveNavMatch | null {
  // Override mode bypasses pathname detection: exact-match the given href
  // against flat entries and group children.
  if (activeHrefOverride !== undefined) {
    if (activeHrefOverride === null) return null;
    for (const link of links) {
      const children = link.children ?? [];
      if (children.length > 0) {
        const child = children.find((c) => c.href === activeHrefOverride);
        if (child) return { key: linkKey(link), href: child.href };
      } else if (link.href === activeHrefOverride) {
        return { key: linkKey(link), href: link.href };
      }
    }
    return null;
  }

  let best: ActiveNavMatch | null = null;
  let bestLen = -1;
  const consider = (owner: ActiveNavLink, candidate: ActiveNavLink) => {
    if (
      matchesPath(candidate.href, pathname, matchSubPaths) &&
      candidate.href.length > bestLen
    ) {
      bestLen = candidate.href.length;
      best = { key: linkKey(owner), href: candidate.href };
    }
  };
  for (const link of links) {
    const children = link.children ?? [];
    if (children.length > 0) {
      for (const child of children) consider(link, child);
    } else {
      consider(link, link);
    }
  }
  return best;
}
